/**
 * Pure helpers for turning the GraphQL `hikeHighlights` payload into
 * `HighlightInput[]` for `projectHighlightsOntoLine`.
 *
 * Extracted from TrailDataCard.vue so the deterministic mapping (coordinate
 * order, type → icon, invalid-row filtering) can be unit tested without a DOM.
 */
import type { HighlightInput } from '../../utils/elevation/projectHighlights';

/** Raw hike-highlight shape as returned by the GraphQL `hikeHighlights` field. */
export interface RawHikeHighlight {
  id?: string | number | null;
  title?: string | null;
  type?: string | null;
  /** `[lon, lat]` (see HikeHighlightType#coordinates). */
  coordinates?: number[] | null;
}

/**
 * Map a HikeHighlight `type` (see HikeHighlight::TYPES) to a Material Icons
 * glyph name. Unknown/absent types fall back to a generic pin.
 */
export function iconForHighlightType(type?: string | null): string {
  switch (type) {
    case 'peaks':
      return 'terrain';
    case 'viewpoint':
      return 'photo_camera';
    case 'water':
      return 'water_drop';
    case 'waterfall':
      return 'water_drop';
    case 'swimming':
      return 'pool';
    case 'campground':
    case 'osmCampsite':
      return 'cabin';
    case 'shelter':
      return 'night_shelter';
    case 'hotel':
    case 'hostel':
    case 'bnb':
    case 'motel':
      return 'hotel';
    case 'restaurant':
    case 'fastfood':
      return 'restaurant';
    case 'coffee':
      return 'local_cafe';
    case 'pub':
      return 'sports_bar';
    case 'shop':
      return 'store';
    case 'town':
      return 'location_city';
    case 'parking':
      return 'local_parking';
    case 'restroom':
      return 'wc';
    case 'picnic':
      return 'deck';
    case 'information':
      return 'info';
    case 'warning':
      return 'warning';
    case 'healthcare':
    case 'pharmacy':
      return 'local_hospital';
    case 'busStops':
    case 'transportStations':
      return 'directions_bus';
    case 'cave':
      return 'landscape';
    case 'history':
      return 'account_balance';
    case 'wildlife':
      return 'pets';
    case 'gate':
      return 'door_front';
    case 'wifi':
      return 'wifi';
    case 'evCharging':
      return 'ev_station';
    case 'playground':
      return 'toys';
    default:
      return 'place';
  }
}

/**
 * Convert GraphQL `hikeHighlights` into `HighlightInput[]` for
 * `projectHighlightsOntoLine`. Rows lacking a valid `[lon, lat]` pair are
 * dropped so they never reach turf.
 */
export function buildHikeHighlightInputs(
  highlights: RawHikeHighlight[] | null | undefined
): HighlightInput[] {
  if (!Array.isArray(highlights)) return [];

  const inputs: HighlightInput[] = [];
  for (const h of highlights) {
    if (!h) continue;
    const coords = h.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) continue;
    const [lon, lat] = coords;
    if (typeof lon !== 'number' || typeof lat !== 'number') continue;
    if (Number.isNaN(lon) || Number.isNaN(lat)) continue;

    inputs.push({
      id: h.id ?? `${lon},${lat}`,
      name: h.title || h.type || 'Highlight',
      lon,
      lat,
      icon: iconForHighlightType(h.type),
    });
  }
  return inputs;
}
